import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react'
//@ts-ignore
import { Avatar } from 'dsm-react-lib'
import ChatMessageContent from './ChatMessageContent'
import UserDropdown from '../../../../@core/layouts/components/navbar/UserDropdown'
import { ChatFeedback } from './ChatFeedback'
import { useSelector } from 'react-redux'
import { useChatService } from '../services/chatService'
import { IconSend, IconChevronDown, IconChevronUp, IconX } from '@tabler/icons-react'
import { ChatbotContext } from '../context/ChatbotProvider'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { appInsights } from '../../../../_services/AppInsights'
import { EventType } from '../../../../utility/types'
import { getInitials } from '../../../../utility/Utils'
import { useSasToken } from '../../../../_hooks/useSasToken'
import { useThemeManager } from '../../../../_hooks/useThemeManager'
import { staticAssetsPath } from '../../../../configs/constants'
import { ThinkingPanel } from '../reasoning'
import { transformToolCallsForDisplay } from '../utils/TextUtils'
import ChartRenderer from '../frontend-tools/tools/visualization/ChartRenderer'

interface ChatBubbleProps {
  message: any
  messageIndex: number
  setIsRegenerating?: any
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  messageIndex,
  setIsRegenerating
}) => {
  const { handleChatSubmit } = useChatService()
  const { activeResponseIndex, role, type, response } = message
  const {
    content,
    id,
    toolCalls: rawToolCalls,
    reasoningContent,
    reasonings
  } = response[activeResponseIndex]
  const isBot = role === 'assistant'
  const botLoading = type === 'loader'
  const { selectedAgent, selectedChatModel } = useSelector((state: any) => state.chatbotReducer)

  const { messages, isStreaming, isThinking, thinkingTimeElapsed, setThinkingTimeElapsed } =
    useContext(ChatbotContext)
  const [showFeedback, setShowFeedback] = useState(isBot || false)
  const isLastMessage = messages?.length - 1 === messageIndex
  const chatContentRef = useRef<any>(null)
  const [isPromptEditing, setIsPromptEditing] = useState(false)
  const [isEditFocused, setIsEditFocused] = useState(false)
  const [editedPrompt, setEditedPrompt] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const isSendDisabled = editedPrompt === '' || editedPrompt === content
  const editPromptRef = useRef<any>(null)
  const thinkingStartRef = useRef<number | null>(null)
  const responseStreamingStartedRef = useRef(false)
  const { appendToken } = useSasToken()
  const chatbotIcon = appendToken(`${staticAssetsPath}/images/logo/ALI_Chatbot-vb.png`)
  //@ts-ignore
  const trackModelVersionUsed = useTrackEvent(appInsights, EventType.ModelVersionUsed)

  // Transform AG UI protocol tool calls to display format.
  // `create_visualization` is a pure rendering tool: it emits a ChartSpec as its
  // arguments and never returns a TOOL_CALL_RESULT, so it would otherwise show as
  // an empty tool-call card. Exclude it here — it renders as an inline chart below.
  const transformedToolCalls = transformToolCallsForDisplay(
    (rawToolCalls || []).filter((tc: any) => tc?.name !== 'create_visualization')
  )
  // Visualization tool calls render an inline chart from their (streamed) arguments.
  const chartSpecs = (rawToolCalls || [])
    .filter((tc: any) => tc?.name === 'create_visualization')
    .map((tc: any) => {
      try {
        const spec =
          typeof tc.arguments === 'string' ? JSON.parse(tc.arguments || '') : tc.arguments
        return { id: tc.id, spec }
      } catch {
        // Arguments still streaming (partial JSON) — render once complete.
        return null
      }
    })
    .filter(Boolean) as { id: string; spec: any }[]
  const elapsedTime =
    isLastMessage && isThinking
      ? thinkingTimeElapsed
      : response[activeResponseIndex]?.elapsedTime || thinkingTimeElapsed
  const { isDarkTheme: isDark } = useThemeManager()
  // isThinking is global to the chat session, so it must be scoped to this bubble being the
  // last message — otherwise every historical bot bubble renders as actively thinking too.
  const isThinkingThisMessage = isThinking && isLastMessage

  // Thinking component timer - only ticks while isThinking; stops as soon as real content arrives.
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined
    if (isThinking && isBot && isLastMessage) {
      if (thinkingStartRef.current === null) {
        // Anchor the start time accounting for any already-elapsed time (e.g. between tool calls)
        // eslint-disable-next-line no-mixed-operators
        thinkingStartRef.current = Date.now() - thinkingTimeElapsed * 1000
      }
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - thinkingStartRef.current!) / 1000)
        setThinkingTimeElapsed(elapsed)
      }, 500)
    } else {
      thinkingStartRef.current = null
    }
    return () => clearInterval(interval)
  }, [isThinking, isLastMessage, isBot])

  useEffect(() => {
    if (isPromptEditing) {
      setEditedPrompt(content)
      setIsEditFocused(true)
    }
  }, [isPromptEditing])

  useEffect(() => {
    setIsPromptEditing(false)
    responseStreamingStartedRef.current = false
  }, [activeResponseIndex])

  useEffect(() => {
    if (editPromptRef.current) {
      editPromptRef.current.style.height = 'auto' // Reset height
      editPromptRef.current.style.height = `${Math.min(
        editPromptRef.current.scrollHeight,
        window.innerHeight * 0.5
      )}px`
      if (editedPrompt !== '') {
        editPromptRef.current.focus()
      }
    }
  }, [editedPrompt, isPromptEditing])

  useEffect(() => {
    if (!isBot || !isLastMessage) {
      return
    }

    const responseHasStarted = Boolean(isStreaming && content)

    if (responseHasStarted && !responseStreamingStartedRef.current) {
      // Collapse once when response text starts; later tokens must not override user expansion.
      setIsExpanded(false)
    } else if (isThinking && !responseHasStarted) {
      // Reasoning/tool-call phase: keep expanded so thoughts are visible.
      setIsExpanded(true)
    }

    responseStreamingStartedRef.current = responseHasStarted
  }, [isBot, isLastMessage, isThinking, isStreaming, content])

  const handleToggle = () => {
    setIsExpanded((expanded) => !expanded)
  }

  const formatTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds}s`
    }
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  const getThinkingMessage = () => {
    // If no tool calls yet, show generic thinking message
    if (transformedToolCalls.length === 0) {
      return 'Thinking'
    }

    // Find the most recent running tool, or the last tool
    const runningTool = transformedToolCalls.find((tc) => tc.status === 'running')
    const currentTool = runningTool || transformedToolCalls[transformedToolCalls.length - 1]

    if (!currentTool) {
      return 'Thinking'
    }

    // Map tool types and actions to specific messages
    const toolMessages: Record<string, string> = {
      search: 'Accessing web search...',
      code: 'Executing code...',
      databox: 'Querying databox(es)...',
      dataproduct: 'Querying data product(s)...',
      grep: 'Searching code...',
      read: 'Reading files...',
      edit: 'Editing files...',
      todo: 'Managing tasks...',
      memory: 'Saving memory...'
    }

    // Get the message based on tool type
    let message = toolMessages[currentTool.type]

    // If no predefined message, check if it's a specific action
    if (!message) {
      const action = currentTool.action.toLowerCase()

      // Check for image generation
      if (action.includes('image') || action.includes('generate')) {
        message = 'Generating an image...'
      } else if (action.includes('mcp') || action.includes('text-to-sql')) {
        // Check for MCP or custom tools
        message = `Calling ${currentTool.action}...`
      } else if (action.includes('semantic')) {
        // Check for semantic search
        message = 'Searching workspace...'
      } else {
        // Default fallback
        message = `Calling ${currentTool.action}...`
      }
    }

    return message
  }

  const getChatBotAvatar = () => {
    const isActivelyStreaming = (isStreaming || isThinking) && isLastMessage
    const avatarContent = selectedAgent ? (
      selectedAgent.avatarUrl ? (
        <img
          src={selectedAgent.avatarUrl}
          className="rounded-circle mt-50"
          width="35px"
          height="35px"
          alt="Avatar"
          data-testid="chat-bubble-avatar"
        />
      ) : (
        <Avatar
          size="28px"
          content={selectedAgent.avatarUrl || getInitials(selectedAgent?.name)}
          className="shrink-0 !m-0"
        />
      )
    ) : (
      <div
        className="w-10 h-10 rounded-full bg-[var(--surface-bg)] flex items-center justify-center"
        data-testid="chat-bubble-avatar">
        <img src={chatbotIcon} alt="Avatar" className="w-8 h-8" />
      </div>
    )

    return isActivelyStreaming ? (
      <div className="chat-avatar-spinner">{avatarContent}</div>
    ) : (
      avatarContent
    )
  }

  const onSubmitEditedPrompt = () => {
    if (!editedPrompt || editedPrompt.trim() === '') {
      return
    }
    setIsPromptEditing(false)
    trackModelVersionUsed({
      chatType: 'edit',
      modelVersion: selectedChatModel?.name
    })
    handleChatSubmit({
      prompt: editedPrompt,
      parentId: message?.parentId,
      isEditPrompt: true,
      messageId: id,
      files: message?.resources || []
    })
  }

  const getEditPromptComponent = () => {
    return (
      <div
        className={`flex w-full items-center p-1 rounded-lg whitespace-pre-wrap transition-all duration-200 theme-user-prompt-surface chat-primary ${
          isEditFocused ? 'ring-1 ring-[var(--brand-accent-color)] ' : ''
        }`}>
        <div className="flex flex-col w-full bg-transparent">
          <div className={`relative ${editedPrompt?.length > 0 ? 'focused' : ''}`}>
            <div className="relative">
              <textarea
                className="w-full border-none outline-none bg-transparent resize-none chat-messages-text-color"
                autoComplete="off"
                value={editedPrompt}
                ref={editPromptRef}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  setEditedPrompt(e.target.value)
                }}
                onFocus={() => setIsEditFocused(true)}
                onBlur={() => setIsEditFocused(false)}
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    if (!isSendDisabled) {
                      onSubmitEditedPrompt()
                    }
                  }
                }}
              />
            </div>
          </div>
          <div className="flex w-full justify-end">
            <div
              className="flex items-center justify-center my-1.5 mx-1.5 cursor-pointer"
              onClick={() => {
                setIsPromptEditing(false)
                setEditedPrompt('')
              }}>
              <IconX size={25} color={'var(--color-secondary)'} />
            </div>
            <div className="flex items-center justify-center my-1.5 mx-0">
              <IconSend
                size={24}
                color={isSendDisabled ? 'var(--text-subheading)' : 'var(--color-primary)'}
                className={`${
                  isSendDisabled ? 'cursor-not-allowed' : 'cursor-pointer send-button'
                }`}
                onClick={() => {
                  if (!isSendDisabled) onSubmitEditedPrompt()
                }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        className={`flex mt-1 ml-1 items-start ${
          isBot ? 'justify-start max-w-[calc(100%-40px)]' : 'justify-end'
        }`}
        data-testid="chat-bubble">
        {isBot && getChatBotAvatar()}
        <div
          className={`flex ml-4 min-w-0 ${
            !isBot && isPromptEditing ? 'w-[calc(100%-92px)]' : 'flex-1'
          }`}
          onMouseEnter={() => {
            if (!isBot) setShowFeedback(true)
          }}
          onMouseLeave={() => {
            if (!isBot) setShowFeedback(false)
          }}>
          <div className={`flex flex-col w-full ${isBot ? 'items-start' : 'items-end'}`}>
            {/* Thinking Header — only shown for reasoning models (isThinking or has reasoningContent) */}
            {isBot &&
              ((isThinking && isLastMessage) ||
                !!reasoningContent ||
                transformedToolCalls.length > 0) && (
                <div className="flex align-left items-center gap-2 mb-2">
                  <div className="flex items-center gap-1">
                    <span
                      className={`text-body-strong ${
                        isThinkingThisMessage
                          ? `text-shimmer animate-text-shimmer ${
                              isDark ? 'text-shimmer-dark' : 'text-shimmer-light'
                            }`
                          : 'text-theme-heading'
                      }`}>
                      {isThinkingThisMessage ? getThinkingMessage() : 'Thoughts'}
                    </span>
                    {/* <span
                    className={`font-semibold text-sm ${
                      isDark ? 'text-[var(--text-disabled)]' : 'text-[var(--icon-muted)]'
                    }`}>
                    â€¢
                  </span>
                  <span
                    className={`font-semibold text-sm leading-[1.571em] ${
                      isDark ? 'text-[var(--text-disabled)]' : 'text-[var(--icon-muted)]'
                    }`}>
                    {elapsedTime === 0 ? '<1s' : `${formatTime(elapsedTime)}`}
                  </span> */}
                  </div>
                  {(transformedToolCalls.length > 0 || !!reasoningContent) && (
                    <button
                      className={`flex items-center justify-center w-5 h-5 bg-transparent border-none cursor-pointer p-0 transition-colors duration-200 text-theme-heading hover:text-theme-subheading`}
                      onClick={handleToggle}
                      aria-label={
                        isExpanded ? 'Collapse thinking process' : 'Expand thinking process'
                      }>
                      {isExpanded ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
                    </button>
                  )}
                </div>
              )}
            {/* Show thinking component when tool calls exist or during streaming */}
            {isBot && (
              <ThinkingPanel
                isThinking={isThinkingThisMessage || (isStreaming && isLastMessage)}
                toolCalls={transformedToolCalls}
                isExpanded={isExpanded}
                reasonings={reasonings}
                showLatestRunningBadge={isLastMessage && isThinkingThisMessage && !content}
              />
            )}
            {isPromptEditing && getEditPromptComponent()}
            {content || message?.resources?.length > 0 ? (
              <ChatMessageContent
                isPromptEditing={isPromptEditing}
                message={message}
                nodeRef={chatContentRef}
              />
            ) : (
              <div data-testid="chat-bubble-empty" />
            )}
            {isBot &&
              chartSpecs.map((chart) => (
                <ChartRenderer key={chart.id} spec={chart.spec} isDark={isDark} />
              ))}
            {response[activeResponseIndex]?.id !== null && !botLoading && (
              <ChatFeedback
                showFeedback={
                  (showFeedback && !isStreaming) || isBot || message?.response?.length > 1
                }
                messageObject={message}
                setIsRegenerating={setIsRegenerating}
                chatContentRef={chatContentRef}
                setIsPromptEditing={setIsPromptEditing}
              />
            )}
          </div>
        </div>
        {!isBot && (
          <div data-testid="chat-bubble-avatar">
            <UserDropdown onlyAvatar />
          </div>
        )}
      </div>
    </>
  )
}
